import { max as maxOf, min as minOf } from "lodash";

export type Scale = "linear" | "log";

export interface Bin {
  // The start of this bin's range (inclusive)
  rangeStart: number;
  // The end of this bin's range (exclusive, except for the last bin)
  rangeEnd: number;
  // The x-axis position to plot this bin at. How it's derived from the bin's range depends on the
  // scale: the midpoint of rangeStart/rangeEnd for a linear bin, or the midpoint of their log10
  // values for a log bin.
  plotValue: number;
  // The number of values that fall within this bin's range.
  count: number;
}

/**
 * Checks whether a list of values can be meaningfully shown on a log scale.
 *
 * It requires that:
 *  - The list is non-empty
 *  - Every value is greater than 0 (log10 is undefined for non-positive values)
 *  - Not every value is identical (there's no range to express on a log scale)
 *
 * @param values - The list of values to check
 * @returns true if the values can be shown on a log scale
 */
export function canUseLogScale(values: number[]): boolean {
  const domainMin = minOf(values);
  const domainMax = maxOf(values);
  return domainMin !== undefined && domainMax !== undefined && domainMin > 0 && domainMin !== domainMax;
}

/**
 * Bins the given values into a fixed number of equal-width bins.
 *
 * @param values - The list of numbers to bin.
 * @param binCount - The number of bins to create. Must be a positive, finite number.
 * @returns the bins, ordered from lowest to highest range. Empty if invalid.
 *
 * @example
 *   calculateLinearBins([1, 25, 50, 75, 99], 4)
 *   // [
 *   //   { rangeStart: 1, rangeEnd: 25.5, plotValue: 13.25, count: 2 },
 *   //   { rangeStart: 25.5, rangeEnd: 50, plotValue: 37.75, count: 0 },
 *   //   { rangeStart: 50, rangeEnd: 74.5, plotValue: 62.25, count: 1 },
 *   //   { rangeStart: 74.5, rangeEnd: 99, plotValue: 86.75, count: 2 }
 *   // ]
 */
export function calculateLinearBins(values: number[], binCount: number): Bin[] {
  if (!Number.isFinite(binCount) || binCount <= 0) return [];

  const domainMin = minOf(values);
  const domainMax = maxOf(values);
  if (domainMin === undefined || domainMax === undefined) return [];

  // Default to 1 to avoid divide-by-zero when all values are identical. This will make all values fall into the first bin.
  const binWidth = Math.max(domainMax - domainMin, 1) / binCount;

  const bins: Bin[] = Array.from({ length: binCount }, (_, index) => {
    const rangeStart = domainMin + index * binWidth;
    const rangeEnd = rangeStart + binWidth;
    return { plotValue: (rangeStart + rangeEnd) / 2, rangeStart, rangeEnd, count: 0 };
  });

  values.forEach((value) => {
    const index = Math.min(Math.floor((value - domainMin) / binWidth), binCount - 1);
    bins[index].count += 1;
  });

  return bins;
}

/**
 * Buckets a list of numbers into a fixed number of bins that are equal-width in log10 space
 *
 * @param values - The list of numbers to bin.
 * @param binCount - The number of bins to create. Must be a positive, finite number.
 * @returns the bins, ordered from lowest to highest range, or `null` if invalid.
 *
 * @example
 *   calculateLogBins([1, 10, 100, 1000], 3)
 *   // [
 *   //   { rangeStart: 1, rangeEnd: 10, plotValue: 0.5, count: 1 },
 *   //   { rangeStart: 10, rangeEnd: 100, plotValue: 1.5, count: 1 },
 *   //   { rangeStart: 100, rangeEnd: 1000, plotValue: 2.5, count: 2 }
 *   // ]
 */
export function calculateLogBins(values: number[], binCount: number): Bin[] | null {
  if (!Number.isFinite(binCount) || binCount <= 0) return null;
  if (!canUseLogScale(values)) return null;

  const domainMin = minOf(values) as number;
  const domainMax = maxOf(values) as number;
  const logMin = Math.log10(domainMin);
  const logBinWidth = (Math.log10(domainMax) - logMin) / binCount;

  const bins: Bin[] = Array.from({ length: binCount }, (_, index) => {
    const rangeStart = 10 ** (logMin + index * logBinWidth);
    const rangeEnd = 10 ** (logMin + (index + 1) * logBinWidth);
    const plotValue = logMin + (index + 0.5) * logBinWidth;
    return { rangeStart, rangeEnd, plotValue, count: 0 };
  });

  values.forEach((value) => {
    const index = Math.min(Math.floor((Math.log10(value) - logMin) / logBinWidth), binCount - 1);
    bins[Math.max(index, 0)].count += 1;
  });

  return bins;
}
